import type React from 'react';
import { motion } from 'framer-motion';
import { staggerCards, cardItem, fadeInUp } from '../utils/animations';
import Section from './ui/Section';
import SectionHeading from './ui/SectionHeading';
import Icon from './ui/Icon';
import Button from './ui/Button';
import { SERVICES_DATA } from '../utils/constants';

const Services: React.FC = () => {
  const handleLearnMore = (serviceId: string) => {
    // Could be expanded to navigate to a detailed service page
    const serviceElement = document.getElementById(serviceId);
    if (serviceElement) {
      serviceElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Section id="services" bgColor="gray">
      <SectionHeading
        title="Комплексные IT-решения для вашего бизнеса"
        subtitle="Мы предлагаем полный спектр услуг по проектированию, внедрению и обслуживанию IT-инфраструктуры"
      />

      <motion.div
        className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
        variants={staggerCards}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {SERVICES_DATA.map((service) => (
          <motion.div
            key={service.id}
            id={service.id}
            className={`group relative overflow-hidden rounded-lg ${
              service.highlight ? 'bg-primary text-white' : 'bg-white text-gray-800'
            } p-6 shadow-md transition-all duration-300 hover:shadow-lg`}
            variants={cardItem}
          >
            {/* Accent diagonal line for highlighted services */}
            {service.highlight && (
              <div className="absolute -right-4 -top-4 h-16 w-16 rotate-45 bg-secondary opacity-50" />
            )}

            <div className="relative">
              {/* Icon */}
              <div
                className={`mb-4 inline-flex rounded-lg p-3 ${
                  service.highlight
                    ? 'bg-white/10 text-white'
                    : 'bg-primary/10 text-primary'
                }`}
              >
                <Icon name={service.icon} size={32} />
              </div>

              {/* Title */}
              <h3 className={`mb-4 text-xl font-bold ${
                service.highlight ? 'text-white' : 'text-gray-900'
              }`}>
                {service.title}
                {service.highlight && (
                  <span className="ml-2 rounded-full bg-secondary px-2 py-1 text-xs font-medium text-white">
                    Популярная
                  </span>
                )}
              </h3>

              {/* Description */}
              <p className={`mb-6 ${
                service.highlight ? 'text-gray-100' : 'text-gray-600'
              }`}>
                {service.description}
              </p>

              {/* Features (could be added later) */}
              <div className="mt-auto">
                <Button
                  variant={service.highlight ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={() => {
                    const contactForm = document.getElementById('main-cta');
                    contactForm?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Заказать услугу
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        className="mt-12 text-center"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <p className="mb-6 text-lg text-gray-700">
          Не нашли нужную услугу? Свяжитесь с нами для индивидуального решения!
        </p>
        <Button
          variant="primary"
          onClick={() => {
            const contactForm = document.getElementById('main-cta');
            contactForm?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Обсудить проект
        </Button>
      </motion.div>
    </Section>
  );
};

export default Services;
